package chat.amp.tim

import androidx.compose.ui.graphics.Color
import de.connect2x.messenger.compose.view.common.deriveFromHue
import de.connect2x.messenger.compose.view.common.hue
import org.jetbrains.compose.resources.DrawableResource

/**
 * The whitelabel variant this build was made for. The values come from `brands/<id>/brand.toml`
 * and reach the code as a generated `Brand` value; see docs/variantenmanagement.md.
 *
 * A brand never brings its own code: everything a customer can influence is a value here, and
 * optional functionality is switched on and off through [BrandFeatures].
 */
data class BrandSpec(
    val id: String,
    val urls: BrandUrls,
    val tim: BrandTim,
    val features: BrandFeatures,
    /** Logos in the room list header, in the order they were declared. May be empty. */
    val logos: List<BrandLogo>,
    val palette: BrandPalette,
)

data class BrandUrls(
    val privacy: String,
    val imprint: String,
    val push: String,
    val versionCheck: String,
)

data class BrandTim(
    val clientId: String,
)

data class BrandFeatures(
    /** Route media downloads through the scanning [ampDownloadManagerImpl]. */
    val contentScanning: Boolean,
    /** Hide every download action. `-Ptim_disable_downloads=true` forces this on. */
    val downloadsDisabled: Boolean,
    /** Which of the shared component sets styles the client. */
    val themeComponents: ThemeComponentSet,
)

/**
 * The component sets that ship with the app. They are named after the design they
 * implement, not after a customer - every brand may pick any of them.
 */
enum class ThemeComponentSet {
    /** The messenger library's own styling. */
    MESSENGER,

    /** Pill shaped room list rows and a tinted sidebar, from the cherry design. */
    CHERRY,

    /** White surfaces, strong selection colour, pale own messages, filled date labels. */
    FLAT,
}

enum class LogoSlot { START, END }

data class BrandLogo(
    val resource: DrawableResource,
    val slot: LogoSlot,
    val heightDp: Int,
)

/**
 * A single Material colour role. Brands either pin a colour or let it follow the accent colour
 * the user picked at runtime - the AMP palette does the latter for most of its roles.
 */
sealed interface BrandColor {
    /** Always this colour. */
    data class Fixed(val color: Color) : BrandColor

    /** This colour's saturation and lightness, with the hue of the runtime accent colour. */
    data class HueFromAccent(val color: Color) : BrandColor

    /** The runtime accent colour itself. */
    data object FromAccent : BrandColor

    fun resolve(accent: Color): Color = when (this) {
        is Fixed -> color
        is HueFromAccent -> color.deriveFromHue(accent.hue)
        FromAccent -> accent
    }
}

data class BrandPalette(
    /** The accent colour a fresh installation starts with. */
    val accent: Color,
    val light: BrandColors,
    val dark: BrandColors,
)

/** The Material 3 colour roles of one scheme. */
data class BrandColors(
    val primary: BrandColor,
    val onPrimary: BrandColor,
    val primaryContainer: BrandColor,
    val onPrimaryContainer: BrandColor,
    val secondary: BrandColor,
    val onSecondary: BrandColor,
    val secondaryContainer: BrandColor,
    val onSecondaryContainer: BrandColor,
    val tertiary: BrandColor,
    val onTertiary: BrandColor,
    val tertiaryContainer: BrandColor,
    val onTertiaryContainer: BrandColor,
    val error: BrandColor,
    val errorContainer: BrandColor,
    val onError: BrandColor,
    val onErrorContainer: BrandColor,
    val background: BrandColor,
    val onBackground: BrandColor,
    val surface: BrandColor,
    val onSurface: BrandColor,
    val surfaceVariant: BrandColor,
    val onSurfaceVariant: BrandColor,
    val outline: BrandColor,
    val inverseOnSurface: BrandColor,
    val inverseSurface: BrandColor,
    val inversePrimary: BrandColor,
    val surfaceTint: BrandColor,
    val outlineVariant: BrandColor,
    val scrim: BrandColor,
    // Material's newer surface container roles. A brand may leave them out, then Material
    // computes its own - which is its purple tinted baseline, not the brand's neutral.
    val surfaceDim: BrandColor? = null,
    val surfaceBright: BrandColor? = null,
    val surfaceContainerLowest: BrandColor? = null,
    val surfaceContainerLow: BrandColor? = null,
    val surfaceContainer: BrandColor? = null,
    val surfaceContainerHigh: BrandColor? = null,
    val surfaceContainerHighest: BrandColor? = null,
)
